/**
 * Clipboard + download helpers for the breakdown exports.
 *
 * Browsers only honour clipboard writes while the user's click is still
 * "active", and Safari drops that activation as soon as the handler awaits
 * anything. The exports all do real work first (a fetch, an html2canvas
 * render), so the write is STARTED synchronously with a promise-valued
 * ClipboardItem — the browser waits for the data itself and the gesture is
 * preserved. Where ClipboardItem is missing the helpers fall back to writing the
 * resolved value (Chrome/Firefox accept that from a focused tab), and finally to
 * letting the user copy by hand.
 */
import React from "react";
import { Modal } from "antd";
import { saveAs } from "file-saver";

function clipboardApi() {
  return typeof navigator !== "undefined" ? navigator.clipboard : undefined;
}

export function supportsClipboardItem() {
  const clip = clipboardApi();
  return !!clip && typeof clip.write === "function" && typeof window !== "undefined"
    && typeof window.ClipboardItem === "function";
}

async function writeItem(mime, valuePromise) {
  const clip = clipboardApi();
  const ClipboardItemCtor = typeof window !== "undefined" ? window.ClipboardItem : undefined;
  if (clip && typeof clip.write === "function" && typeof ClipboardItemCtor === "function") {
    try {
      // Promise-valued item: the gesture-safe path.
      await clip.write([new ClipboardItemCtor({ [mime]: valuePromise })]);
      return true;
    } catch (err) {
      // Older implementations reject promise values with a TypeError; retry
      // with the resolved blob before giving up.
      try {
        const blob = await valuePromise;
        await clip.write([new ClipboardItemCtor({ [mime]: blob })]);
        return true;
      } catch (err2) {
        return false;
      }
    }
  }
  return false;
}

/**
 * Copy a PNG (or other blob) to the clipboard. `blobPromise` may still be
 * rendering. Resolves true when the clipboard took it, false when the browser
 * refused or cannot hold images (the caller then relies on the download).
 */
export async function copyBlobToClipboard(mime, blobPromise) {
  return writeItem(mime, Promise.resolve(blobPromise));
}

/**
 * Copy text to the clipboard. `textPromise` may still be building. Resolves
 * true on success; false when every path was refused (the caller may then show
 * the manual-copy dialog).
 */
export async function copyTextToClipboard(textPromise) {
  const textP = Promise.resolve(textPromise);
  const ok = await writeItem("text/plain", textP.then((t) => new Blob([String(t)], { type: "text/plain" })));
  if (ok) return true;
  const clip = clipboardApi();
  if (clip && typeof clip.writeText === "function") {
    try {
      await clip.writeText(String(await textP));
      return true;
    } catch (err) {
      return false;
    }
  }
  return false;
}

/** Save a blob to the user's Downloads folder. */
export function downloadBlob(blob, filename) {
  saveAs(blob, filename);
}

/** Last resort for text: a dialog with the text pre-selected for Ctrl/Cmd+C. */
export function showManualCopy(text, { title = "Copy manually" } = {}) {
  Modal.info({
    title,
    width: 720,
    okText: "Done",
    content: (
      <div>
        <p style={{ marginBottom: 8 }}>
          The browser blocked copying to the clipboard. Select the text below and press Ctrl+C (⌘C on Mac).
        </p>
        <textarea
          readOnly
          value={String(text)}
          rows={14}
          style={{ width: "100%", fontFamily: "monospace", fontSize: 12, whiteSpace: "pre" }}
          onFocus={(e) => e.target.select()}
          ref={(el) => { if (el) setTimeout(() => { try { el.focus(); el.select(); } catch (e) { /* noop */ } }, 0); }}
        />
      </div>
    ),
  });
}
